import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { serialize } from 'object-to-formdata';
import { DialogName, getDialogComponent } from '../../workflow-dialogs/dialog-dictionary';
import { CaseTableItem } from '../../payee-cases/types';
import { environment } from 'src/environments/environment';

export type RunTransition = {
  caseId: string;
  transitionId: string;
  params?: Record<string | number, unknown>;
};

export type RunTransitionWithDialog = {
  caseId: string;
  dialog: DialogName;
  transitionId: string;
  media?: {
    uuid: string;
    file_name: string;
    storage_url: string;
  }[];
  extraParams?: Record<string, unknown>;
};

export type RunTransitionResponse = {
  payee_case: CaseTableItem;
  transition_at: string | null;
};

export type FullPaymentTransitionResponse = {
  case_id: string;
  transition_id: string;
  dialog: DialogName;
};

export type CloseTransitionResponse = {
  case_id: string;
  transition_id: string;
  dialog: DialogName;
};

@Injectable({
  providedIn: 'root'
})
export class WorkflowService {
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  readonly afterWorkflow = new Subject<RunTransitionResponse>();

  showTransitionError(error: unknown): void {
    console.error('Error while running transition', error);
    const message: string = error instanceof AxiosError && error.response?.data?.message
      || this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');
    this.snackbar.open(message, this.translate.instant('OK'), { duration: 10_000 });
  }

  async runTransition({
    transitionId,
    caseId,
    params,
  }: RunTransition): Promise<RunTransitionResponse> {
    const formData = serialize({ params }, {
      booleansAsIntegers: true,
      indices: true,
      nullsAsUndefineds: true,
    });
    formData.append('transitionClass', transitionId);

    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${environment.baseUrl}/api/transition/${caseId}`,
      data: formData,
    };
    const result = await axios(config);
    this.afterWorkflow.next(result.data);
    return result.data;
  }

  openTransitionDialog(data: RunTransitionWithDialog): void {
    const dialogComponent = getDialogComponent(data.dialog);
    this.dialog.open(dialogComponent, {
      data,
      autoFocus: false,
      maxWidth: '90vw',
    });
  }

  async getDialogData<T = unknown>({ caseId, transitionId, params }: RunTransition): Promise<T> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${environment.baseUrl}/api/transition/${caseId}/dialog`,
      data: {
        transitionClass: transitionId,
        ...(params ? { params } : {}),
      },
    };
    const result = await axios(config);
    return result.data;
  }

  async getFullPaymentTransition(
    payeeCaseReferenceId: string,
  ): Promise<FullPaymentTransitionResponse> {
    const result = await axios.get<FullPaymentTransitionResponse>(
      `${environment.baseUrl}/api/transition/full-payment/${encodeURIComponent(payeeCaseReferenceId)}`,
    );
    return result.data;
  }

  async getCloseTransition(
    payeeCaseReferenceId: string,
  ): Promise<CloseTransitionResponse> {
    const result = await axios.get<CloseTransitionResponse>(
      `${environment.baseUrl}/api/transition/close/${encodeURIComponent(payeeCaseReferenceId)}`,
    );
    return result.data;
  }
}
